import { Button, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import classnames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BudgetSummaryStep } from 'src/Components/Expenses/BudgetSummaryStep';
import { BudgetDraft } from 'src/Components/Expenses/BudgetWizard/BudgetWizard.types';
import {
  buildDraft,
  computeDraftTotals,
  draftToLines,
} from 'src/Components/Expenses/BudgetWizard/BudgetWizard.utils';
import { DailyLifeEnvelopeStep } from 'src/Components/Expenses/DailyLifeEnvelopeStep';
import { FixedCostsStep } from 'src/Components/Expenses/FixedCostsStep';
import { IncomeStep } from 'src/Components/Expenses/IncomeStep';
import { ProjectsChecklistStep } from 'src/Components/Expenses/ProjectsChecklistStep';
import useSaveBudgetYear from 'src/Hooks/Mutations/useSaveBudgetYear';
import useBudgetSuggestions from 'src/Hooks/Queries/useBudgetSuggestions';
import useBudgetYear from 'src/Hooks/Queries/useBudgetYear';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { trackBudgetWizardSaved, trackBudgetWizardStarted } from 'src/Services/Mixpanel/events';
import { ApiError } from 'src/types/api-error.types';

import styles from './BudgetWizard.module.scss';
import { Props } from './BudgetWizard.types';

const STEP_COUNT = 5;

const BudgetWizard = ({ year, onSaved }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const previousYear = year - 1;

  const { data: budgetYear, isLoading: isYearLoading } = useBudgetYear(year);
  const { data: suggestions, isLoading: areSuggestionsLoading } = useBudgetSuggestions(year);
  const saveBudgetYear = useSaveBudgetYear();

  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<BudgetDraft | null>(null);

  useEffect(() => {
    if (!assetId) return;
    trackBudgetWizardStarted({ asset_id: assetId, year });
  }, [assetId, year]);

  // The draft is seeded once per year from the API, then owned by the wizard: refetches must
  // not overwrite what the manager is in the middle of typing.
  useEffect(() => {
    if (!budgetYear || !suggestions) return;
    setDraft(buildDraft(budgetYear, suggestions));
    setStep(0);
  }, [budgetYear?.year, suggestions?.year]); // eslint-disable-line react-hooks/exhaustive-deps

  const householdCount = budgetYear?.householdCount ?? 0;
  const totals = useMemo(
    () => (draft ? computeDraftTotals(draft, householdCount) : null),
    [draft, householdCount]
  );

  if (isYearLoading || areSuggestionsLoading || !draft || !totals || !budgetYear || !suggestions) {
    return (
      <div className={styles.loading}>
        <Loader size="sm" />
      </div>
    );
  }

  const stepTitles = [
    t('budgetV2.step.fixedCosts', 'Frais fixes'),
    t('budgetV2.step.dailyLife', 'Vie quotidienne'),
    t('budgetV2.step.projects', 'Travaux & projets'),
    t('budgetV2.step.incomes', 'Recettes'),
    t('budgetV2.step.summary', 'Synthèse'),
  ];

  const stepSubtitles = [
    t(
      'budgetV2.step.fixedCostsHelp',
      'Suggérés depuis vos dépenses {{year}}. Décochez ce qui ne revient pas, ajustez les montants.',
      { year: previousYear }
    ),
    t(
      'budgetV2.step.dailyLifeHelp',
      "Une enveloppe globale pour l'année : courses, gaz, petit entretien, produits ménagers…"
    ),
    t(
      'budgetV2.step.projectsHelp',
      'Vos projets « À voter » et de la Boîte à idées. Cochez ceux à budgéter cette année.'
    ),
    t('budgetV2.step.incomesHelp', 'Appels de fonds, locations et recettes diverses.'),
    t('budgetV2.step.summaryHelp', 'Vérifiez votre budget avant de l’enregistrer.'),
  ];

  const save = () => {
    const lines = draftToLines(draft);
    saveBudgetYear.mutate(
      { year, lines },
      {
        onSuccess: () => {
          if (assetId) {
            trackBudgetWizardSaved({
              asset_id: assetId,
              year,
              lines_count: lines.length,
              expense_total: totals.expenseTotal,
              income_total: totals.incomeTotal,
            });
          }
          notifications.show({
            color: 'green',
            message: t('budgetV2.saved', 'Budget {{year}} enregistré.', { year }),
          });
          onSaved();
        },
        onError: (error: unknown) => {
          const response = (error as AxiosError<ApiError>)?.response;
          const isClientError = !!response && response.status < 500;
          if (!isClientError) {
            Sentry.captureException(error, { tags: { assetId, year } });
          }
          notifications.show({
            color: 'red',
            message:
              isClientError && response.data?.message
                ? response.data.message
                : t(
                    'budgetV2.saveServerError',
                    "Impossible d'enregistrer le budget (erreur serveur). Réessayez, et si ça persiste contactez-nous."
                  ),
          });
        },
      }
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.stepper}>
        {stepTitles.map((title, index) => (
          <button
            key={title}
            type="button"
            className={classnames(styles.stepPill, {
              [styles.stepPillActive]: index === step,
              [styles.stepPillDone]: index < step,
            })}
            onClick={() => setStep(index)}
          >
            <span className={styles.stepIndex}>{index < step ? '✓' : index + 1}</span>
            {title}
          </button>
        ))}
      </div>

      <div className={styles.card}>
        <h3 className={styles.stepTitle}>{stepTitles[step]}</h3>
        <p className={styles.stepSubtitle}>{stepSubtitles[step]}</p>

        {step === 0 ? (
          <FixedCostsStep
            previousYear={previousYear}
            rows={draft.fixedCosts}
            onChange={fixedCosts => setDraft({ ...draft, fixedCosts })}
          />
        ) : null}

        {step === 1 ? (
          <DailyLifeEnvelopeStep
            previousYear={previousYear}
            draft={draft.dailyLife}
            suggestion={suggestions.dailyLife}
            onChange={dailyLife => setDraft({ ...draft, dailyLife })}
          />
        ) : null}

        {step === 2 ? (
          <ProjectsChecklistStep
            rows={draft.projects}
            onChange={projects => setDraft({ ...draft, projects })}
          />
        ) : null}

        {step === 3 ? (
          <IncomeStep
            year={year}
            rows={draft.incomes}
            householdCount={householdCount}
            financingNeed={totals.financingNeed}
            onChange={incomes => setDraft({ ...draft, incomes })}
          />
        ) : null}

        {step === 4 ? (
          <BudgetSummaryStep
            year={year}
            previousYear={previousYear}
            totals={totals}
            householdShares={budgetYear.householdShares}
            isGovernanceBased={budgetYear.isGovernanceBased}
            householdSharesTotal={budgetYear.householdSharesTotal}
          />
        ) : null}
      </div>

      <div className={styles.nav}>
        {step > 0 ? (
          <Button variant="outline" size="md" onClick={() => setStep(step - 1)}>
            {t('budgetV2.previous', '← Précédent')}
          </Button>
        ) : (
          <span />
        )}
        {step < STEP_COUNT - 1 ? (
          <Button size="md" onClick={() => setStep(step + 1)}>
            {t('budgetV2.next', 'Suivant →')}
          </Button>
        ) : (
          <Button size="md" onClick={save} loading={saveBudgetYear.isLoading}>
            {t('budgetV2.save', '✓ Enregistrer mon budget')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BudgetWizard;
